import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Col
} from 'reactstrap';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  auditorProcessAssign,
  processAuditorAssignedListGet,
  processAuditorNotAssignedListGet
} from '../../../store/slices/tenantSlices/configurationSlice';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'react-toastify';
import FormInput from '../../../components/form/FormInput';

const AssignAuditorProcessModal = ({ isOpen, onCloseClick, process_id = null }) => {
  const dispatch = useDispatch();
  const [availableAuditor, setAvailableAuditor] = useState([]);
  const [assignedAuditor, setAssignedAuditor] = useState([]);
  const [searchAvailable, setSearchAvailable] = useState('');
  const [searchAssigned, setSearchAssigned] = useState('');
  const [filteredAvailableAuditor, setFilteredAvailableAuditor] = useState([]);
  const [filteredAssignedAuditor, setFilteredAssignedAuditor] = useState([]);

  useEffect(() => {
    if (process_id && isOpen) {
      fetchProcessAuditorAssignedList();
      fetchProcessAuditorNotAssignedList();
    }
  }, [process_id, isOpen]);

  const fetchProcessAuditorNotAssignedList = async () => {
    try {
      const response = await dispatch(processAuditorNotAssignedListGet({ process_id })).unwrap();
      if (response) {
        setAvailableAuditor(response?.data || []);
        setFilteredAvailableAuditor(response?.data || []);
      }
    } catch (error) {
      console.log('Error fetching available Auditor:', error);
    }
  };

  const fetchProcessAuditorAssignedList = async () => {
    try {
      const response = await dispatch(processAuditorAssignedListGet({ process_id })).unwrap();
      if (response) {
        setAssignedAuditor(response?.data || []);
        setFilteredAssignedAuditor(response?.data || []);
      }
    } catch (error) {
      console.log('Error fetching assigned Auditor:', error);
    }
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;

    if (!destination) return;

    const updatedAvailableAuditor = [...availableAuditor];
    const updatedAssignedAuditor = [...assignedAuditor];

    if (source.droppableId === 'available' && destination.droppableId === 'assigned') {
      const movedItem = filteredAvailableAuditor[source.index];
      updatedAvailableAuditor.splice(updatedAvailableAuditor.indexOf(movedItem), 1);
      updatedAssignedAuditor.splice(destination.index, 0, movedItem);
    }

    if (source.droppableId === 'assigned' && destination.droppableId === 'available') {
      const movedItem = filteredAssignedAuditor[source.index];
      updatedAssignedAuditor.splice(updatedAssignedAuditor.indexOf(movedItem), 1);
      updatedAvailableAuditor.splice(destination.index, 0, movedItem);
    }

    setAvailableAuditor(updatedAvailableAuditor);
    setAssignedAuditor(updatedAssignedAuditor);
    setFilteredAvailableAuditor(
      updatedAvailableAuditor.filter((item) =>
        item.category_name.toLowerCase().includes(searchAvailable.toLowerCase())
      )
    );
    setFilteredAssignedAuditor(
      updatedAssignedAuditor.filter((item) =>
        item.category_name.toLowerCase().includes(searchAssigned.toLowerCase())
      )
    );
  };

  const onSubmit = async () => {
    try {
      const queue_ids = assignedAuditor.map((x) => x.auditor_category_id);
      const payload = {
        auditor_category_ids: queue_ids,
        process_id: process_id
      };
      const response = await dispatch(auditorProcessAssign(payload)).unwrap();
      toast.success(response.message);
      onCloseClick();
      setAvailableAuditor([]);
      setAssignedAuditor([]);
    } catch (error) {
      console.log('Error during queue assignment:', error);
    }
  };

  useEffect(() => {
    setFilteredAvailableAuditor(
      availableAuditor.filter((item) =>
        item.category_name.toLowerCase().includes(searchAvailable.toLowerCase())
      )
    );
  }, [searchAvailable, availableAuditor]);

  useEffect(() => {
    setFilteredAssignedAuditor(
      assignedAuditor.filter((item) =>
        item.category_name.toLowerCase().includes(searchAssigned.toLowerCase())
      )
    );
  }, [searchAssigned, assignedAuditor]);

  useEffect(() => {
    if (!isOpen) {
      setAvailableAuditor([]);
      setAssignedAuditor([]);
      setFilteredAvailableAuditor([]);
      setFilteredAssignedAuditor([]);
      setSearchAvailable('');
      setSearchAssigned('');
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">Assign Auditor</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <Row className="py-6">
          <DragDropContext onDragEnd={onDragEnd}>
            <Row className="py-6">
              <Col xs="6" md="6" sm="6" lg="6" className="mr-10">
                <Card>
                  <CardHeader className="bg-info py-2 text-dark text-center text-white">
                    <div>Available Auditor</div>
                  </CardHeader>
                  <CardBody
                    className="bg-dark-950"
                    style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto', height: 'auto' }}>
                    <FormInput
                      placeholder="Search available Auditor"
                      value={searchAvailable}
                      onChange={(e) => setSearchAvailable(e.target.value)}
                      className="mb-3"
                    />
                    <Droppable droppableId="available">
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={
                            filteredAvailableAuditor.length === 0
                              ? 'p-2 border mb-3 bg-white rounded-2 text-primary'
                              : ''
                          }>
                          {filteredAvailableAuditor.length > 0 ? (
                            filteredAvailableAuditor.map((item, index) => (
                              <Draggable
                                key={item.auditor_category_id}
                                draggableId={String(item.auditor_category_id)}
                                index={index}>
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className="p-2 border mb-3 bg-white rounded-2 text-primary">
                                    {item.category_name || '-'}
                                  </div>
                                )}
                              </Draggable>
                            ))
                          ) : (
                            <div>No available Auditor</div>
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </CardBody>
                </Card>
              </Col>

              <Col xs="6" md="6" sm="6" lg="6">
                <Card>
                  <CardHeader className="bg-primary py-2 text-dark text-center text-white">
                    <div>Assigned Auditor</div>
                  </CardHeader>
                  <CardBody
                    className="bg-dark-950"
                    style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto', height: 'auto' }}>
                    <FormInput
                      placeholder="Search assigned Auditor"
                      value={searchAssigned}
                      onChange={(e) => setSearchAssigned(e.target.value)}
                      className="mb-3"
                    />
                    <Droppable droppableId="assigned">
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={
                            filteredAssignedAuditor.length === 0
                              ? 'p-2 border mb-3 bg-white rounded-2 text-primary'
                              : ''
                          }>
                          {filteredAssignedAuditor.length > 0 ? (
                            filteredAssignedAuditor.map((item, index) => (
                              <Draggable
                                key={item.auditor_category_id}
                                draggableId={String(item.auditor_category_id)}
                                index={index}>
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className="p-2 border mb-3 bg-white rounded-2 text-primary">
                                    {item.category_name || '-'}
                                  </div>
                                )}
                              </Draggable>
                            ))
                          ) : (
                            <div>No assigned Auditor</div>
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </DragDropContext>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            setAvailableAuditor([]);
            setAssignedAuditor([]);
            setFilteredAvailableAuditor([]);
            setFilteredAssignedAuditor([]);
            setSearchAvailable('');
            setSearchAssigned('');
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button
          form="Auditor"
          type="submit"
          className="px-5 bg-primary border-primary"
          onClick={onSubmit}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AssignAuditorProcessModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onCloseClick: PropTypes.func.isRequired,
  process_id: PropTypes.any
};

export default AssignAuditorProcessModal;
